import { ApexBarChart } from '@/components'
import { useResponsive } from '@/hooks/useResponsive'
import { generatePeriod } from '@/pages/main/_components/golfSearch/useGolfSearchData'
import {
  GraphDataState,
  MonthAvgPriceByRegionItem,
} from '@/redux/slices/golfSearchSlice'
import styled from 'styled-components'

interface MonthGraphProps {
  monthData: GraphDataState['monthData']
  monthAvgPriceByRegion?: GraphDataState['monthAvgPriceByRegion']
  isDualGraph?: boolean
  areaCd?: string | null
  areaNm?: string
  bizNm?: string
}

export const MonthGraph = ({
  monthData,
  monthAvgPriceByRegion,
  isDualGraph,
  areaCd,
  areaNm,
  bizNm,
}: MonthGraphProps) => {
  const safeGraphData = monthData && monthData.length ? monthData : []
  const safeRegionGraphData =
    monthAvgPriceByRegion && monthAvgPriceByRegion.length
      ? monthAvgPriceByRegion
      : []

  const monthsArr = generatePeriod('month')

  const mapRegionAvgToGraph = (
    regionData: MonthAvgPriceByRegionItem[] = [],
    monthsArr: string[] = [],
  ): number[][] => {
    const updatedRegionGraphData = [
      new Array(12).fill(0),
      new Array(12).fill(0),
      new Array(12).fill(0),
      new Array(12).fill(0),
    ]

    regionData.forEach((item) => {
      const formattedMonth = `${item.year.slice(-2)}-${item.month.padStart(2, '0')}`
      const monthIndex = monthsArr.findIndex(
        (month) => month === formattedMonth,
      )

      if (monthIndex >= 0 && monthIndex < 12) {
        updatedRegionGraphData[0][monthIndex] = item.n01
        updatedRegionGraphData[1][monthIndex] = item.n02
        updatedRegionGraphData[2][monthIndex] = item.m01
        updatedRegionGraphData[3][monthIndex] = item.m02
      }
    })

    return updatedRegionGraphData
  }

  const regionGraphData = mapRegionAvgToGraph(safeRegionGraphData, monthsArr)
  const { isMobile, isTablet, isDesktop } = useResponsive()
  const commonOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: false },
    },
    stroke: {
      width: 3,
      curve: 'smooth',
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
      hover: {
        size: 6,
      },
    },
    xaxis: {
      categories: monthsArr,
      labels: {
        rotateAlways: true,
        rotate: -45,
      },
    },
    yaxis: {
      title: { text: '단위 (원)' },
      labels: {
        formatter: (value: any) => value.toLocaleString('ko-KR'),
      },
    },
    tooltip: {
      y: {
        formatter: (value: any) => value.toLocaleString('ko-KR'),
      },
    },
    fill: {
      opacity: [0.85, 0.85, 0.55, 0.45],
      gradient: {
        inverseColors: false,
        shade: 'light',
        type: 'vertical',
        opacityFrom: 0.85,
        opacityTo: 0.55,
      },
    },
  }

  const nonMemberData = [
    {
      name: '비회원(주중)',
      data: safeGraphData[0] || [0],
      color: '#FF6347',
    },
    {
      name: '비회원(주말)',
      data: safeGraphData[1] || [0],
      color: '#4682B4',
    },
    // {
    //   name: `${areaNm} 평균(비회원 주중)`,
    //   data: regionGraphData[0] || [0],
    //   color: '#7B68EE',
    //   type: 'area',
    //   dashArray: 5,
    // },
    // {
    //   name: `${areaNm} 평균(비회원 주말)`,
    //   data: regionGraphData[1] || [0],
    //   color: '#00FA9A',
    //   type: 'area',
    //   dashArray: 5,
    // },
  ]

  const memberData = [
    {
      name: '회원(주중)',
      data: safeGraphData[2] || [0],
      color: '#32CD32',
    },
    {
      name: '회원(주말)',
      data: safeGraphData[3] || [0],
      color: '#FFD700',
    },
    // {
    //   name: `${areaNm} 평균(회원 주중)`,
    //   data: regionGraphData[2] || [0],
    //   color: '#7B68EE',
    //   type: 'line',
    //   dashArray: 5,
    // },
    // {
    //   name: `${areaNm} 평균(회원 주말)`,
    //   data: regionGraphData[3] || [0],
    //   color: '#00FA9A',
    //   type: 'line',
    //   dashArray: 5,
    // },
  ]

  const isNonMemberDataAvailable =
    safeGraphData[0]?.some((value) => value !== 0) ||
    safeGraphData[1]?.some((value) => value !== 0)
  const isMemberDataAvailable =
    safeGraphData[2]?.some((value) => value !== 0) ||
    safeGraphData[3]?.some((value) => value !== 0)

  //월 평균 하나의 차트로 보여주려고 만든 데이터 시작
  // const combinedData = [...nonMemberData, ...memberData]

  // const isDataAvailable =
  //   safeGraphData[0]?.some((v) => v !== 0) ||
  //   safeGraphData[1]?.some((v) => v !== 0) ||
  //   safeGraphData[2]?.some((v) => v !== 0) ||
  //   safeGraphData[3]?.some((v) => v !== 0)
  //월 평균 하나의 차트로 보여주려고 만든 데이터 끝

  return (
    <GraphWrapper>
      <GraphTitle>
        {/* {areaNm ? `${areaNm} 지역 평균` : '월 평균 가격 비교'} */}
        {`${bizNm} (월 평균 가격)`}
      </GraphTitle>

      {/* <GraphRow>
        <GraphBox>
          {isNonMemberDataAvailable ? (
            <ApexBarChart
              options={{
                ...commonOptions,
                title: {
                  text: '비회원',
                  align: 'left',
                  style: { fontSize: '16px', fontWeight: 'bold' },
                },
              }}
              height="350px"
              width="100%"
              series={nonMemberData}
            />
          ) : null}
        </GraphBox>
        <GraphBox>
          {isMemberDataAvailable ? (
            <ApexBarChart
              options={{
                ...commonOptions,
                title: {
                  text: '회원',
                  align: 'left',
                  style: { fontSize: '16px', fontWeight: 'bold' },
                },
              }}
              height="350px"
              width="100%"
              series={memberData}
            />
          ) : null}
        </GraphBox>
      </GraphRow> */}
      <ChartContainer isMobile={isMobile}>
        {isNonMemberDataAvailable && isMemberDataAvailable ? (
          // 둘 다 있을 때 나란히 보여줌
          <div style={{ display: 'flex', gap: '20px' }}>
            <GraphBox>
              <ApexBarChart
                options={{
                  ...commonOptions,
                  title: {
                    text: '비회원',
                    align: 'left',
                    style: { fontSize: '16px', fontWeight: 'bold' },
                  },
                }}
                height="350px"
                width="100%"
                series={nonMemberData}
              />
            </GraphBox>
            <GraphBox>
              <ApexBarChart
                options={{
                  ...commonOptions,
                  title: {
                    text: '회원',
                    align: 'left',
                    style: { fontSize: '16px', fontWeight: 'bold' },
                  },
                }}
                height="350px"
                width="100%"
                series={memberData}
              />
            </GraphBox>
          </div>
        ) : isNonMemberDataAvailable ? (
          // 비회원만 있을 때 넓게 보여줌
          <ApexBarChart
            options={{
              ...commonOptions,
              title: {
                text: '비회원',
                align: 'left',
                style: { fontSize: '16px', fontWeight: 'bold' },
              },
            }}
            height="350px"
            width="100%"
            series={nonMemberData}
          />
        ) : isMemberDataAvailable ? (
          // 회원만 있을 때 넓게 보여줌
          <ApexBarChart
            options={{
              ...commonOptions,
              title: {
                text: '회원',
                align: 'left',
                style: { fontSize: '16px', fontWeight: 'bold' },
              },
            }}
            height="350px"
            width="100%"
            series={memberData}
          />
        ) : null}
      </ChartContainer>
      {/* {isDataAvailable ? (
      //회원+비회원 데이터를 하나의 그래프로 합쳐서 보여줌
        <ApexBarChart
          options={{
            ...commonOptions,
            title: {
              text: '회원 & 비회원',
              align: 'left',
              style: { fontSize: '16px', fontWeight: 'bold' },
            },
          }}
          height="350px"
          width="100%"
          series={combinedData}
        />
      ) : null} */}
    </GraphWrapper>
  )
}

const GraphWrapper = styled.div`
  width: 100%;
  position: relative;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 8px;
  width: 100%;
  margin-bottom: 20px;
`

const GraphTitle = styled.p`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 10px;
`

const GraphRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 15px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const GraphBox = styled.div`
  flex: 1;
`
const ChartContainer = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `
        overflow-x: auto;
        overflow-y: hidden;
        width: 100%;
        padding:15px;
        
        &::-webkit-scrollbar {
          height: 10px;
        }
        
        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
      `
      : `
        width: 100%;
      `}
`
